import React, { useState } from 'react';
import './AwningConfigurator.css';
import { apiFetch } from '../services/api';
import { MADE_TO_ORDER_BANDS } from '../config/leadTimes';
import { formatSaleEnds, fmtPrice } from '../utils/pricing';
import useAwningConfig from '../hooks/useAwningConfig';
import { findColour, swatchStyle } from '../data/sauledaColours';
import ExpressCheckout from './ExpressCheckout';
import AccessoryPicker from './product/AccessoryPicker';

function AwningConfigurator({ product, selectedColour: controlledColour, onColourChange }) {
  // The rules - sizes, prices, stock, lead time, checkout URL - come from the
  // shared hook (also used by the full /configure page). This component owns
  // the compact layout and the quote-request form only.
  const cfg = useAwningConfig(product, { colour: controlledColour, onColourChange });
  const {
    variants, popular, solids, stripes, mostPopularCode,
    widths, projections, available, availableNow, stockWidths, sale,
    selectedWidth, setSelectedWidth, selectedProjection, setSelectedProjection,
    customMode, setCustomMode, customWidth, setCustomWidth, customProjection, setCustomProjection,
    displayWidth, displayProjection,
    selectedColour, setSelectedColour, toggleColour, colourName, colourCode: selectedColourCode,
    price: selectedVariantPrice, variantReady, isInStock, leadTime, checkoutUrl,
    accessories, addonQty, setAddonQty, addons, total,
  } = cfg;

  const [showAll, setShowAll] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [postcode, setPostcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email.'); return; }
    if (!/^\d{4}$/.test(postcode.trim())) { setError('Please enter a 4-digit postcode.'); return; }

    // The form is only reached for unpriced variants (custom sizes, or a
    // standard size with no admin price yet) — it's a price/quote request.
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/contacts/awning-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          postcode: postcode.trim(),
          product_name: product.name,
          product_slug: product.slug || '',
          colour: colourName,
          width: displayWidth,
          projection: displayProjection,
          custom_dimensions: customMode,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Render a swatch button
  const renderSwatch = (v, globalIdx, opts = {}) => {
    // The swatch is drawn from the site-wide colour definition (vector
    // stripes, weave photos); the variant's own `swatch` value is the fallback.
    const def = findColour(v.code);
    const isStripe = def ? !!def.stripes : !!v.swatch?.includes('gradient');
    const showPopularRing = !!opts.markPopular && v.popular;
    // Single "most popular" colour gets a distinct green ring on top of the regular popular ring.
    const isMostPopular = !!mostPopularCode && v.code === mostPopularCode;
    const cls = [
      'ac-swatch',
      globalIdx === selectedColour ? 'ac-swatch--active' : '',
      isStripe ? 'ac-swatch--stripe' : '',
      showPopularRing ? 'ac-swatch--popular' : '',
      isMostPopular ? 'ac-swatch--most-popular' : '',
    ].filter(Boolean).join(' ');
    return (
      <button
        key={v.code || globalIdx}
        type="button"
        className={cls}
        style={def ? swatchStyle(def, { repeatPx: 22 }) : (isStripe ? { background: v.swatch } : { backgroundColor: v.swatch || '#ccc' })}
        onClick={() => toggleColour(globalIdx)}
        aria-label={`${v.name} (${v.code})${v.popular ? ' - popular' : ''}`}
        title={`${v.name}${v.popular ? ' (Popular)' : ''}`}
      />
    );
  };

  if (submitted) {
    return (
      <div className="ac-wrap">
        <div className="ac-success">
          <svg className="ac-check" width="48" height="48" fill="none" viewBox="0 0 24 24" style={{ stroke: 'var(--asg-cta)' }} strokeWidth="2">
            <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <h3>Request sent!</h3>
          <p>We'll email your personalised quote within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ac-wrap">
      <h3 className="ac-heading">Check Availability &amp; Price</h3>

      {/* Available-now overview — at-a-glance in-stock widths + colours,
          straight from inventory so it can't drift out of sync. */}
      {availableNow.length > 0 ? (
        <div className="ac-availability">
          <div className="ac-availability-head">
            <span className="ac-stock-dot" aria-hidden="true"></span>
            Available Now
          </div>
          <ul className="ac-availability-list">
            {availableNow.map((row) => (
              <li key={row.width}>
                <span className="ac-availability-w">{row.width} m</span>
                <span className="ac-availability-c">{row.colours.join(', ')}</span>
              </li>
            ))}
          </ul>
          <p className="ac-availability-note">Other sizes &amp; colours are made to order: {MADE_TO_ORDER_BANDS}.</p>
        </div>
      ) : (
        <div className="ac-availability ac-availability--empty">
          No stock ready to ship right now. Pick a size &amp; colour to request an availability ETA.
        </div>
      )}

      {/* Colour picker */}
      {variants.length > 0 && (
        <div className="ac-section">
          <label className="ac-label">
            Fabric colour:{' '}
            <span className="ac-value">
              {colourName || <em className="ac-value-empty">none selected</em>}
            </span>
            {variants[selectedColour]?.popular && <span className="ac-popular-tag">Popular</span>}
            {selectedColour !== null && (
              <button
                type="button"
                className="ac-clear-btn"
                onClick={() => setSelectedColour(null)}
              >Clear</button>
            )}
          </label>

          {/* Popular swatches — always visible */}
          {popular.length > 0 && (
            <div className="ac-swatch-group">
              <span className="ac-group-label">Popular</span>
              <div className="ac-swatches">
                {popular.map((v) => renderSwatch(v, variants.indexOf(v)))}
              </div>
            </div>
          )}

          {/* All other swatches — expandable */}
          {!showAll ? (
            <button
              type="button"
              className="ac-show-all"
              onClick={() => setShowAll(true)}
            >
              View all {variants.length} colours
            </button>
          ) : (
            <>
              {solids.length > 0 && (
                <div className="ac-swatch-group">
                  <span className="ac-group-label">Solid Colours</span>
                  <div className="ac-swatches">
                    {solids.map((v) => renderSwatch(v, variants.indexOf(v)))}
                  </div>
                </div>
              )}
              {stripes.length > 0 && (
                <div className="ac-swatch-group">
                  <span className="ac-group-label">Striped Colours</span>
                  <div className="ac-swatches">
                    {stripes.map((v) => renderSwatch(v, variants.indexOf(v)))}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="ac-show-all"
                onClick={() => setShowAll(false)}
              >
                Show less
              </button>
            </>
          )}
        </div>
      )}

      {/* Dimension picker */}
      {widths.length > 0 && (
        <div className="ac-section">
          <label className="ac-label">
            Dimensions{selectedWidth && selectedProjection && !customMode
              ? <span className="ac-value">{selectedWidth} m × {selectedProjection} m</span>
              : customMode && customWidth && customProjection
                ? <span className="ac-value">{customWidth} m × {customProjection} m (custom)</span>
                : null}
          </label>
          {!customMode ? (
            <>
              <div className="ac-size-chips" role="radiogroup" aria-label="Awning size variation">
                {widths.map((w, wIdx) => {
                  // Find the single available projection for this width (post-migration there's one).
                  let maxP = null;
                  for (let pIdx = 0; pIdx < projections.length; pIdx++) {
                    if (available[pIdx] && available[pIdx][wIdx] && (maxP === null || projections[pIdx] > maxP)) {
                      maxP = projections[pIdx];
                    }
                  }
                  if (maxP === null) return null;
                  const sel = String(w) === selectedWidth && String(maxP) === selectedProjection;
                  const hasStock = stockWidths.has(Number(w));
                  return (
                    <button
                      key={w}
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      className={`ac-size-chip${sel ? ' ac-size-chip--active' : ''}${hasStock ? ' ac-size-chip--instock' : ''}`}
                      title={hasStock ? 'In stock in one or more colours' : undefined}
                      onClick={() => { setSelectedWidth(String(w)); setSelectedProjection(String(maxP)); }}
                    >
                      {hasStock && <span className="ac-chip-dot" aria-hidden="true"></span>}
                      {w}×{maxP} m
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="ac-custom-toggle"
                onClick={() => setCustomMode(true)}
              >
                Need custom dimensions?
              </button>
            </>
          ) : (
            <>
              <div className="ac-dim-row">
                <div className="ac-dim-field">
                  <span className="ac-dim-label">Width (m)</span>
                  <input
                    type="text"
                    className="ac-input"
                    placeholder="e.g. 4.2"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(e.target.value)}
                    inputMode="decimal"
                  />
                </div>
                <span className="ac-dim-x">&times;</span>
                <div className="ac-dim-field">
                  <span className="ac-dim-label">Projection (m)</span>
                  <input
                    type="text"
                    className="ac-input"
                    placeholder="e.g. 2.8"
                    value={customProjection}
                    onChange={(e) => setCustomProjection(e.target.value)}
                    inputMode="decimal"
                  />
                </div>
              </div>
              <button
                type="button"
                className="ac-custom-toggle"
                onClick={() => setCustomMode(false)}
              >
                &larr; Back to standard sizes
              </button>
            </>
          )}
        </div>
      )}

      {/* Optional accessories - visible before a size is chosen, so the
          visitor sees what can go with the awning (and what is included). */}
      {accessories.length > 0 && (
        <div className="ac-section">
          <label className="ac-label">
            Optional accessories
            {addons.total > 0 && <span className="ac-value">+${fmtPrice(addons.total)}</span>}
          </label>
          <AccessoryPicker
            options={accessories}
            quantities={addonQty}
            onChange={setAddonQty}
            productName={product.name.split(' ')[0]}
          />
        </div>
      )}

      {/* Selected-variant price (when admin has set one). */}
      {selectedVariantPrice != null && (
        <>
          {sale.active && sale.rrp != null && sale.rrp > selectedVariantPrice && (
            <div className="ac-price-was">${fmtPrice(sale.rrp)}</div>
          )}
          <div className="ac-price">${fmtPrice(total)}</div>
          {addons.total > 0 && (
            <div className="ac-price-breakdown">
              Awning ${fmtPrice(selectedVariantPrice)} + accessories ${fmtPrice(addons.total)}
            </div>
          )}
          {sale.active && sale.end && (
            <div className="ac-sale-ends">{sale.label ? `${sale.label} - ` : ''}{formatSaleEnds(sale.end)}</div>
          )}
          <div className="ac-gst-note">All prices include GST.</div>
          <div className="ac-perk-line">
            <span>Free delivery to most of Melbourne</span>
            <span
              className="ac-perk-info"
              tabIndex={0}
              aria-label="Delivery details"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" />
                <line x1="8" y1="7" x2="8" y2="11.5" strokeLinecap="round" />
                <circle cx="8" cy="4.75" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <span className="ac-perk-tooltip" role="tooltip">
                Free delivery to most of Melbourne, $150 flat to the outer suburbs, and freight
                quoted elsewhere. Check your postcode above, or see the delivery page.
              </span>
            </span>
          </div>
        </>
      )}

      {/* Availability for the selected variant. In stock ships fast; anything
          else is made to order with a lead time — and is still orderable. */}
      {variantReady && (
        isInStock ? (
          <div className="ac-stock ac-stock--in-stock">
            <span className="ac-stock-dot" aria-hidden="true"></span>
            In Stock - Ships Next Business Day
          </div>
        ) : (
          <div className="ac-mto">
            <div className="ac-mto-head">Made to Order - Estimated Lead Time: {leadTime}</div>
            <p className="ac-mto-body">
              This size/colour combination is not currently held as ready stock and will be prepared specifically for
              your order. Our team will assemble the required materials and components before dispatch.
            </p>
            <p className="ac-mto-body">
              You&rsquo;ll receive weekly order updates so you can follow the progress of your order through production
              and dispatch.
            </p>
          </div>
        )
      )}

      {/* CTA. Every variant is orderable:
            • Priced variant (in stock OR made to order) → checkout.
            • Unpriced variant (custom size, or a standard size with no price
              set yet) → request a quote.
          Until a size AND colour are chosen we can't identify the variant. */}
      {!showForm ? (
        !variantReady ? (
          <>
            <button className="ac-cta" disabled type="button" style={{ opacity: 0.4 }}>
              Check Availability
            </button>
            <p className="ac-hint">
              {customMode
                ? 'Enter your custom width and projection.'
                : 'Select a size and colour to check availability.'}
            </p>
          </>
        ) : selectedVariantPrice != null ? (
          <>
            <a
              className="ac-cta"
              href={checkoutUrl}
              style={{ textAlign: 'center', textDecoration: 'none' }}
              rel="nofollow"
            >
              Add to Cart
            </a>
            {/* Express wallets (Apple Pay / Google Pay) for this exact variant.
                Same server-side payment path as the full checkout. */}
            <ExpressCheckout
              product={product}
              width={selectedWidth}
              projection={selectedProjection}
              colourName={colourName}
              colourCode={selectedColourCode}
              addons={addons.lines}
              addonQty={addonQty}
              amountCents={Math.round(total * 100)}
            />
          </>
        ) : (
          <button className="ac-cta" onClick={() => setShowForm(true)}>
            Request a Quote
          </button>
        )
      ) : (
        <form className="ac-form" onSubmit={handleSubmit}>
          <div className="ac-summary">
            <span>{colourName || 'No colour preference'}</span>
            <span className="ac-summary-dot">&bull;</span>
            <span>{displayWidth} m &times; {displayProjection} m</span>
            {customMode && <span className="ac-badge">Custom</span>}
          </div>
          <input
            type="email"
            className="ac-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <input
            type="text"
            className="ac-input"
            placeholder="Postcode"
            maxLength={4}
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
          />
          {error && <p className="ac-error">{error}</p>}
          <button
            type="submit"
            className="ac-submit"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Price Request'}
          </button>
          <button
            type="button"
            className="ac-back-btn"
            onClick={() => { setShowForm(false); setError(''); }}
          >
            &larr; Back
          </button>
        </form>
      )}
    </div>
  );
}

export default AwningConfigurator;
